import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ManagementPanelContextType, ManagementPanelStore, ManagementPanelNavigationState, ManagementPanelBreadcrumb } from './types';

const ManagementPanelContext = createContext<ManagementPanelContextType | undefined>(undefined);

export interface ManagementPanelProviderProps {
  children: ReactNode;
}

export const ManagementPanelProvider: React.FC<ManagementPanelProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [store, setStore] = useState<ManagementPanelStore | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<ManagementPanelStore[]>([]);

  const navigate = useCallback((state: ManagementPanelNavigationState) => {
    setStore(prevStore => {
      let newHistory = navigationHistory;
      
      if (prevStore) {
        newHistory = [...navigationHistory, prevStore];
        setNavigationHistory(newHistory);
      }

      const breadcrumbs: ManagementPanelBreadcrumb[] = newHistory.map(item => ({
        mode: item.mode,
        type: item.type || '',
        value: item.value || '',
        label: item.value || ''
      }));

      return {
        mode: state.mode,
        type: state.type,
        value: state.value,
        data: state.data,
        breadcrumbs
      };
    });
    setIsOpen(true);
  }, [navigationHistory]);

  const navigateToBreadcrumb = useCallback((breadcrumbIndex: number) => {
    if (!store?.breadcrumbs || breadcrumbIndex < 0) return;

    const targetIndex = breadcrumbIndex;
    
    if (targetIndex >= navigationHistory.length) return;

    const targetStore = navigationHistory[targetIndex];
    
    const newHistory = navigationHistory.slice(0, targetIndex);
    setNavigationHistory(newHistory);

    const newBreadcrumbs: ManagementPanelBreadcrumb[] = newHistory.map(item => ({
      mode: item.mode,
      type: item.type || '',
      value: item.value || '',
      label: item.value || ''
    }));

    setStore({
      ...targetStore,
      breadcrumbs: newBreadcrumbs
    });
  }, [store, navigationHistory]);

  const close = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setStore(null);
      setNavigationHistory([]);
    }, 300);
  }, []);

  const actions = { navigate, navigateToBreadcrumb, close };

  return (
    <ManagementPanelContext.Provider value={{ isOpen, store, actions }}>
      {children}
    </ManagementPanelContext.Provider>
  );
};

export const useManagementPanel = (): ManagementPanelContextType => {
  const context = useContext(ManagementPanelContext);
  if (!context) {
    throw new Error('useManagementPanel must be used within a ManagementPanelProvider');
  }
  return context;
};


